/**
 * 슬라이드 PPTX 내보내기 — 클라이언트 사이드.
 * master-of-slide 의 export-pptx.ts 패턴: 각 React 페이지를 PNG 로 캡처해 PPTX 슬라이드 한 장씩 임베드.
 *
 * 호출자는 미리 mount 해둔 1920×1080 페이지 노드 배열 + speaker notes 를 넘긴다.
 * (Editor 가 이미 PDF 인쇄용 hidden 컨테이너를 portal 로 렌더 중 — 같은 노드를 재사용한다.)
 */

import { toPng } from 'html-to-image';
import PptxGenJS from 'pptxgenjs';

const SLIDE_W_INCH = 13.333; // 16:9 와이드스크린 (1920px ≈ 13.333in @ 144 dpi 환산이지만 PPTX 표준 1280×720 풀폭).
const SLIDE_H_INCH = 7.5;

export interface PptxPage {
  /** 1920×1080 페이지 콘텐츠가 마운트된 DOM 노드. */
  node: HTMLElement;
  /** speaker notes (선택). */
  notes?: string | null;
}

export interface ExportPptxOptions {
  filename?: string;
  /** 슬라이드 제목 (PPTX meta). */
  title?: string;
}

/**
 * 페이지 배열을 PPTX 로 변환해 다운로드 트리거.
 * 페이지마다 toPng 로 캡처 → pptxgenjs 슬라이드에 풀폭 image + notes 첨부 → save.
 */
export async function exportPptx(pages: PptxPage[], opts: ExportPptxOptions = {}): Promise<void> {
  if (pages.length === 0) {
    throw new Error('내보낼 페이지가 없습니다.');
  }

  // 폰트 로드 안정화.
  if ('fonts' in document) {
    try {
      await (document as Document & { fonts: { ready: Promise<unknown> } }).fonts.ready;
    } catch {
      // ignore
    }
  }
  await new Promise((r) => requestAnimationFrame(() => r(undefined)));

  const pptx = new PptxGenJS();
  pptx.layout = 'LAYOUT_WIDE'; // 13.333 × 7.5 inch
  if (opts.title) pptx.title = opts.title;

  for (let i = 0; i < pages.length; i += 1) {
    const p = pages[i];
    if (!p) continue;
    const png = await captureNodeAsPng(p.node);
    const slide = pptx.addSlide();
    slide.background = { color: 'FFFFFF' };
    slide.addImage({
      data: png,
      x: 0,
      y: 0,
      w: SLIDE_W_INCH,
      h: SLIDE_H_INCH,
    });
    if (p.notes && p.notes.trim().length > 0) {
      slide.addNotes(p.notes);
    }
  }

  const filename = opts.filename ?? buildDefaultFilename(opts.title);
  await pptx.writeFile({ fileName: filename });
}

async function captureNodeAsPng(node: HTMLElement): Promise<string> {
  // 1920×1080 절대 사이즈로 강제. node 가 이미 이 크기라 가정.
  const dataUrl = await toPng(node, {
    width: 1920,
    height: 1080,
    pixelRatio: 1,
    cacheBust: false,
    backgroundColor: '#ffffff',
    // 자산 (이미지) 은 동일 origin (allow-credentials) 라 자동으로 fetch.
    fetchRequestInit: { credentials: 'include' },
  });
  return dataUrl;
}

function buildDefaultFilename(title?: string): string {
  const base = (title ?? 'slides').replace(/[^A-Za-z0-9가-힣\-_ ]+/g, '_').slice(0, 60);
  const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
  return `${base}-${ts}.pptx`;
}
